import { VideoStream } from '../../components/types/videos';
import { GetOnsiteLocationsForMeeting, GetSrtStreamsForMeeting } from '../../connection/video';
import { EntityType } from '../../types/enums';
import { OnsiteLocationStream, OnsiteLocationSession, OnsiteLocation, Resolution } from '../types/video';
import { AsyncAction } from '../types/types';

interface Setting {
	type: string,
	payload: any
}

export const SET_SELECTED_VIDEO = 'SET_SELECTED_VIDEO';
export const SET_SELECTED_AUDIO = 'SET_SELECTED_AUDIO';
export const SET_DISPLAY_NAME = 'SET_DISPLAY_NAME';
export const SET_CONNECTION_READY = 'SET_CONNECTION_READY';
export const SET_SELECTED_VIDEOS = 'SET_SELECTED_VIDEOS';
export const SET_MAIN_VIDEO_STREAM = 'SET_MAIN_VIDEO_STREAM';
export const SET_SHARING_SCREEN = 'SET_SHARING_SCREEN';
export const SET_CONNECTED_PRESENTER_VIDEOS = 'SET_CONNECTED_PRESENTER_VIDEOS';
export const SET_CONNECTED_MEDIA_VIDEOS = 'SET_CONNECTED_MEDIA_VIDEOS';
export const SET_SELECTED_AUDIO_OUTPUT = 'SET_SELECTED_AUDIO_OUTPUT';
export const DESTROY_MAIN_VIDEO_STREAM = 'DESTROY_MAIN_VIDEO_STREAM';

export const GET_SRT_STREAMS = 'GET_SRT_STREAMS';
export const UPSERT_SRT_STREAM = EntityType.srtStream;
export const DELETE_SRT_STREAM = `${EntityType.srtStream}-delete`;
export const SET_SELECTED_SRT_STREAM_ID = 'SET_SELECTED_SRT_STREAM_ID';
export const GET_ONSITE_LOCATIONS = 'GET_ONSITE_LOCATIONS';
export const UPSERT_ONSITE_LOCATION = EntityType.onsiteLocation;
export const DELETE_ONSITE_LOCATION = `${EntityType.onsiteLocation}-delete`;
export const ADD_ONSITE_LOCATION_STREAM = 'ADD_ONSITE_LOCATION_STREAM';
export const SET_ONSITE_LOCATION_STREAMS = 'SET_ONSITE_LOCATION_STREAM';
export const REMOVE_ONSITE_LOCATION_STREAM = 'REMOVE_ONSITE_LOCATION_STREAM';
export const RESET_ONSITE_LOCATION_STREAMS = 'RESET_ONSITE_LOCATION_STREAMS';
export const SET_ONSITE_LOCATION_SESSION = 'SET_ONSITE_LOCATION_SESSION';
export const SET_SELECTED_ONSITE_LOCATION_ID = 'SET_SELECTED_ONSITE_LOCATION_ID';
export const UPSERT_ONSITE_LOCATION_LOCAL = 'UPSERT_ONSITE_LOCATION_LOCAL';

export const SET_SCREENSHARE_VIDEO_SOURCE = 'SET_SCREENSHARE_VIDEO_SOURCE';

export function setSelectedVideo(device: MediaDeviceInfo): Setting {
	return {
		type: SET_SELECTED_VIDEO,
		payload: device,
	};
}

export function setConnectedPresenterVideos(streams: VideoStream[]): Setting {
	return {
		type: SET_CONNECTED_PRESENTER_VIDEOS,
		payload: streams,
	};
}

export function setConnectedMediaVideos(streams: VideoStream[]): Setting {
	return {
		type: SET_CONNECTED_MEDIA_VIDEOS,
		payload: streams,
	};
}

export function setSelectedAudio(device: MediaDeviceInfo): Setting {
	return {
		type: SET_SELECTED_AUDIO,
		payload: device,
	};
}

export function setSelectedAudioOutput(device: MediaDeviceInfo): Setting {
	return {
		type: SET_SELECTED_AUDIO_OUTPUT,
		payload: device,
	};
}

export function setDisplayName(name: string): Setting {
	return {
		type: SET_DISPLAY_NAME,
		payload: name,
	};
}

export function setConnectionReady(ready: boolean): Setting {
	console.log('setting connection ready', ready);
	return {
		type: SET_CONNECTION_READY,
		payload: ready,
	};
}

export function setMainVideoStream(stream: MediaStream | null): Setting {
	return {
		type: SET_MAIN_VIDEO_STREAM,
		payload: stream,
	};
}

export function destroyMainVideoStream(): Setting {
	return {
		type: DESTROY_MAIN_VIDEO_STREAM,
		payload: null,
	};
}

export function setSharingScreen(open: boolean): Setting {
	return {
		type: SET_SHARING_SCREEN,
		payload: open,
	};
}

export const SET_VIDEO_DEVICES = 'SET_VIDEO_DEVICES';
export function setVideoDevices(devices: MediaDeviceInfo[]): Setting {
	return {
		type: SET_VIDEO_DEVICES,
		payload: devices,
	};
}

export const SET_AUDIO_DEVICES = 'SET_AUDIO_DEVICES';
export function setAudioDevices(devices: MediaDeviceInfo[]): Setting {
	return {
		type: SET_AUDIO_DEVICES,
		payload: devices,
	};
}

export const SET_AUDIO_OUTPUTS = 'SET_AUDIO_OUTPUTS';
export function setAudioOutputs(devices: MediaDeviceInfo[]): Setting {
	return {
		type: SET_AUDIO_OUTPUTS,
		payload: devices,
	};
}

export const SET_SELECTED_RESOLUTION = 'SET_SELECTED_RESOLUTION';
export function setSelectedResolution(resolution: Resolution): Setting {
	return {
		type: SET_SELECTED_RESOLUTION,
		payload: resolution,
	};
}

export const SET_LIVE = 'SET_LIVE';
export function setLive(isLive: boolean): Setting {
	return {
		type: SET_LIVE,
		payload: isLive,
	};
}

export const UPDATE_LOCAL_ID = 'UPDATE_LOCAL_ID';
export function updateLocalId(id: string | number) {
	return {
		type: UPDATE_LOCAL_ID,
		payload: id,
	};
}

export const SET_VIDEO_MUTE = 'SET_VIDEO_MUTE';
export function setVideoMute(muted: boolean) {
	return {
		type: SET_VIDEO_MUTE,
		payload: muted,
	};
}

export const SET_AUDIO_MUTE = 'SET_AUDIO_MUTE';
export function setAudioMute(muted: boolean) {
	return {
		type: SET_AUDIO_MUTE,
		payload: muted,
	};
}

export const SET_PREVIEW_MODE = 'SET_PREVIEW_MODE';
export function setPreviewMode(isPreviewing: boolean) {
	return {
		type: SET_PREVIEW_MODE,
		payload: isPreviewing,
	};
}

export const SET_LIVE_VIDEO_MAP = 'SET_LIVE_VIDEO_MAP';
export function setLiveVideoMap(videos: string[]) {
	return {
		type: SET_LIVE_VIDEO_MAP,
		payload: videos,
	};
}

export const SET_HIDDEN_TITLES = 'SET_HIDDEN_TITLES';
export function setHiddenTitles(profiles: Array<number>) {
	return {
		type: SET_HIDDEN_TITLES,
		payload: profiles,
	};
}

export const SET_USE_MOBILE = 'SET_USE_MOBILE';
export function setUseMobile(useMobile: boolean) {
	return {
		type: SET_USE_MOBILE,
		payload: useMobile,
	};
}

export const RESET_AUDIO_MUTED_PREFS = 'RESET_AUDIO_MUTED_PREFS';
export function resetAudioMutedPrefs() {
	return {
		type: RESET_AUDIO_MUTED_PREFS,
		payload: null,
	};
}

export const SYNC_VIDEO_REDUCER_STATE = 'SYNC_VIDEO_REDUCER_STATE';
export function syncVideoReducerState(state: any) {
	return {
		type: SYNC_VIDEO_REDUCER_STATE,
		payload: state,
	};
}

export const ADD_PRESENTER_VIDEO = 'ADD_PRESENTER_VIDEO';
export function addPresenterVideo(presenter: any) {
	return {
		type: ADD_PRESENTER_VIDEO,
		payload: presenter,
	};
}

export const REMOVE_PRESENTER_VIDEO = 'REMOVE_PRESENTER_VIDEO';
export function removePresenterVideo(id: string) {
	return {
		type: REMOVE_PRESENTER_VIDEO,
		payload: id,
	};
}

export const SET_IS_INITIAL_DEVICE_LOAD = 'SET_IS_INITIAL_DEVICE_LOAD';
export function setIsInitialDeviceLoad(isInitialDeviceLoad: boolean) {
	return {
		type: SET_IS_INITIAL_DEVICE_LOAD,
		payload: isInitialDeviceLoad,
	};
}

export const SET_FEED_VIDEO_MUTE = 'SET_FEED_VIDEO_MUTE';
export function setFeedVideoMute(webRTCId: string, muted: boolean) {
	return {
		type: SET_FEED_VIDEO_MUTE,
		payload: { webRTCId, muted },
	};
}

export const SET_FEED_AUDIO_MUTE = 'SET_FEED_AUDIO_MUTE';
export function setFeedAudioMute(webRTCId: string, muted: boolean) {
	return {
		type: SET_FEED_AUDIO_MUTE,
		payload: { webRTCId, muted },
	};
}

export const ADD_FEED_WEBRTCID = 'ADD_FEED_WEBRTCID';
export function addFeedWebRTCId(webRTCId: string) {
	return {
		type: ADD_FEED_WEBRTCID,
		payload: webRTCId,
	};
}

export const REMOVE_FEED_WEBRTCID = 'REMOVE_FEED_WEBRTCID';
export function removeFeedWebRTCId(webRTCId: string) {
	return {
		type: REMOVE_FEED_WEBRTCID,
		payload: webRTCId,
	};
}

export const CLEAR_FEED_WEBRTCIDS = 'CLEAR_FEED_WEBRTCIDS';
export function clearFeedWebRTCIds() {
	return {
		type: CLEAR_FEED_WEBRTCIDS,
		payload: null,
	};
}

export const SET_SHOULD_REPUBLISH_VIDEO = 'SET_SHOULD_REPUBLISH_VIDEO';
export function setShouldRepublishVideo(shouldRepublishVideo: boolean) {
	return {
		type: SET_SHOULD_REPUBLISH_VIDEO,
		payload: shouldRepublishVideo,
	};
}

export function getSrtStreams(token: string, meetingId: number): AsyncAction {
	return {
		type: GET_SRT_STREAMS,
		promise: GetSrtStreamsForMeeting(token, meetingId),
	};
}

export function setSelectedSrtStreamId(streamId: number) {
	return {
		type: SET_SELECTED_SRT_STREAM_ID,
		payload: streamId,
	};
}

export function getOnsiteLocations(token: string, meetingId: number): AsyncAction {
	return {
		type: GET_ONSITE_LOCATIONS,
		promise: GetOnsiteLocationsForMeeting(token, meetingId),
	};
}

export function addOnsiteLocationStream(id: number, stream: OnsiteLocationStream) {
	return {
		type: ADD_ONSITE_LOCATION_STREAM,
		payload: { id, stream },
	};
}

export function setOnsiteLocationStreams(id: number, streams: OnsiteLocationStream[]) {
	return {
		type: SET_ONSITE_LOCATION_STREAMS,
		payload: { id, streams },
	};
}

export function removeOnsiteLocationStream(id: number, webrtcId: string) {
	return {
		type: REMOVE_ONSITE_LOCATION_STREAM,
		payload: { id, webrtcId },
	};
}

export function resetOnsiteLocationStreams() {
	return {
		type: RESET_ONSITE_LOCATION_STREAMS,
		payload: null,
	};
}

export function setOnsiteLocationSession(onsiteLocationSession: OnsiteLocationSession) {
	return {
		type: SET_ONSITE_LOCATION_SESSION,
		payload: onsiteLocationSession,
	};
}

export function setSelectedOnsiteLocationId(locationId: number) {
	return {
		type: SET_SELECTED_ONSITE_LOCATION_ID,
		payload: locationId,
	};
}

export function upsertOnsiteLocationLocal(location: OnsiteLocation) {
	return {
		type: UPSERT_ONSITE_LOCATION_LOCAL,
		payload: location,
	};
}

export function setScreenshareVideoSource(deviceId: string | null) {
	return {
		type: SET_SCREENSHARE_VIDEO_SOURCE,
		payload: deviceId,
	};
}
